import React from "react";

/**
 * Panel for Laboratory 2 · Perspective.
 *
 * Deliberately exposes cognitive variables — not engine values.
 * Every label here is a question about perception, not a graphics
 * knob.
 */

export default function PerspectivePanel({
  params,
  onParamChange,
  onReset,
  onScreenshot,
  onHideUI,
  readouts,
}) {
  return (
    <aside
      data-testid="perspective-debug-panel"
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        bottom: 16,
        width: 320,
        overflowY: "auto",
        background: "rgba(6,6,10,0.82)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 6,
        padding: "16px 14px 20px",
        color: "#d6d3d1",
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        fontSize: 11,
        lineHeight: 1.5,
        zIndex: 10,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 14,
          paddingBottom: 10,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <span
          style={{
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#a8a29e",
            fontSize: 10,
          }}
        >
          Lab 2 · Perspective
        </span>
        <button
          onClick={onHideUI}
          data-testid="btn-hide-ui"
          style={ghostButtonStyle}
          title="Hide UI (H)"
        >
          hide (H)
        </button>
      </header>

      <p
        style={{
          margin: "0 0 14px",
          color: "#a8a29e",
          fontSize: 10,
          lineHeight: 1.6,
          fontStyle: "italic",
        }}
      >
        Can perspective itself become the interface? Move only the
        perspective. Nothing else — no color, opacity, scale, or
        lighting changes.
      </p>

      {/* Mode toggle — the central experiment switch. */}
      <SectionHeader title="Mode" />
      <div style={{ display: "flex", gap: 6, marginTop: 6, marginBottom: 14 }}>
        <ModeButton
          active={params.mode === "camera-moves"}
          onClick={() => onParamChange("mode", "camera-moves")}
          testId="mode-camera"
        >
          observer withdraws
        </ModeButton>
        <ModeButton
          active={params.mode === "world-moves"}
          onClick={() => onParamChange("mode", "world-moves")}
          testId="mode-world"
        >
          world reorganizes
        </ModeButton>
      </div>

      {/* The primary control. */}
      <SectionHeader title="Perspective" />
      <CogControl
        keyName="perspectiveScale"
        label="Perspective Scale"
        note="How far along the journey the observer has traveled."
        value={params.perspectiveScale}
        min={0}
        max={1}
        step={0.001}
        onChange={(v) => onParamChange("perspectiveScale", v)}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, marginBottom: 12 }}>
        <input
          type="checkbox"
          id="auto-animate"
          checked={params.autoAnimate}
          onChange={(e) => onParamChange("autoAnimate", e.target.checked)}
          data-testid="chk-autoanimate"
          style={{ accentColor: "#d9b98a" }}
        />
        <label htmlFor="auto-animate" style={{ color: "#a8a29e", fontSize: 10, cursor: "pointer" }}>
          auto-animate (hands off)
        </label>
      </div>

      <SectionHeader title="Arrangement" />
      <CogControl
        keyName="objectSeparation"
        label="Object Separation"
        note="How far apart the three points sit in space."
        value={params.objectSeparation}
        min={0.3}
        max={3}
        step={0.01}
        onChange={(v) => onParamChange("objectSeparation", v)}
      />
      <CogControl
        keyName="contextRadius"
        label="Context Radius"
        note="How far the observer can withdraw."
        value={params.contextRadius}
        min={1.5}
        max={12}
        step={0.05}
        onChange={(v) => onParamChange("contextRadius", v)}
      />

      <SectionHeader title="Discovery" />
      <CogControl
        keyName="discoveryThreshold2"
        label="Discovery Threshold 2"
        note="Where along the journey the second point emerges."
        value={params.discoveryThreshold2}
        min={0}
        max={1}
        step={0.001}
        onChange={(v) => onParamChange("discoveryThreshold2", v)}
      />
      <CogControl
        keyName="discoveryThreshold3"
        label="Discovery Threshold 3"
        note="Where along the journey the third point emerges."
        value={params.discoveryThreshold3}
        min={0}
        max={1}
        step={0.001}
        onChange={(v) => onParamChange("discoveryThreshold3", v)}
      />

      <SectionHeader title="Readouts" />
      <Readout
        label="Relationship Visibility"
        value={readouts?.relationshipVisibility?.value ?? 0}
        note={
          readouts?.relationshipVisibility
            ? `${readouts.relationshipVisibility.visible} of 3 in view`
            : ""
        }
      />

      <section style={{ marginTop: 18 }}>
        <SectionHeader title="Actions" />
        <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
          <ActionButton onClick={onReset} testId="btn-reset">
            reset scene
          </ActionButton>
          <ActionButton onClick={() => onScreenshot()} testId="btn-screenshot">
            screenshot + metadata
          </ActionButton>
        </div>
      </section>

      <footer
        style={{
          marginTop: 20,
          paddingTop: 10,
          borderTop: "1px solid rgba(255,255,255,0.06)",
          color: "#57534e",
          fontStyle: "italic",
          fontSize: 10,
          lineHeight: 1.6,
        }}
      >
        Success: an observer says "I didn't realize these belonged
        together" without any explicit explanation.
      </footer>
    </aside>
  );
}


function SectionHeader({ title }) {
  return (
    <div
      style={{
        color: "#a8a29e",
        fontSize: 10,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        marginTop: 4,
        marginBottom: 6,
      }}
    >
      {title}
    </div>
  );
}


function CogControl({ keyName, label, note, value, min, max, step, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <label style={{ color: "#d6d3d1", fontSize: 10 }}>{label}</label>
        <span style={{ color: "#78716c", fontSize: 10 }}>{value.toFixed(step < 1 ? 3 : 0)}</span>
      </div>
      {note && (
        <div style={{ color: "#57534e", fontSize: 9, fontStyle: "italic", marginTop: 1, marginBottom: 3 }}>
          {note}
        </div>
      )}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={`control-${keyName}`}
        style={{ width: "100%", accentColor: "#d9b98a", cursor: "pointer" }}
      />
    </div>
  );
}


function Readout({ label, value, note }) {
  const pct = Math.round(value * 100);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <label style={{ color: "#d6d3d1", fontSize: 10 }}>{label}</label>
        <span style={{ color: "#d9b98a", fontSize: 10 }}>{value.toFixed(3)}</span>
      </div>
      {note && (
        <div style={{ color: "#57534e", fontSize: 9, fontStyle: "italic", marginTop: 1, marginBottom: 3 }}>
          {note}
        </div>
      )}
      <div
        style={{
          height: 3,
          background: "rgba(255,255,255,0.05)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: "#d9b98a",
            opacity: 0.7,
            transition: "width 0.12s ease-out",
          }}
        />
      </div>
    </div>
  );
}


function ModeButton({ children, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        flex: 1,
        background: active ? "rgba(217,185,138,0.14)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${active ? "rgba(217,185,138,0.5)" : "rgba(255,255,255,0.12)"}`,
        color: active ? "#f3ead6" : "#a8a29e",
        fontFamily: "inherit",
        fontSize: 10,
        padding: "6px 8px",
        borderRadius: 3,
        cursor: "pointer",
        letterSpacing: "0.06em",
      }}
    >
      {children}
    </button>
  );
}


function ActionButton({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#d6d3d1",
        fontFamily: "inherit",
        fontSize: 10,
        padding: "6px 10px",
        borderRadius: 3,
        cursor: "pointer",
        letterSpacing: "0.08em",
        textAlign: "left",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.03)")}
    >
      {children}
    </button>
  );
}


const ghostButtonStyle = {
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "#a8a29e",
  fontFamily: "inherit",
  fontSize: 10,
  padding: "2px 8px",
  borderRadius: 3,
  cursor: "pointer",
  letterSpacing: "0.08em",
};
